import { useContext, useEffect, useState, useRef } from "react";
import { AppContext } from "../App";
import { callApi, getInlineLoader } from "../Helpers";
import { FiFileText, FiUploadCloud, FiCheckCircle, FiArrowRight } from "react-icons/fi";

export default function CreateApplication({ uuid }) {
  const appContext = useContext(AppContext);

  /* Place states here */
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);

  const [data, setData] = useState(null);
  const [cvName, setCvName] = useState("");
  const [coverName, setCoverName] = useState("");
  const [certName, setCertName] = useState("");

  /* DOM Reference Tracking Blocks */
  const cvInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const certInputRef = useRef(null);

  async function init() {
    // run initializations here
    setReady(false);
    await Promise.all([
      (async () => {
        if (uuid) {
          await getData();
        }
      })(),
    ]);
    setReady(true);
  }

  async function getData() {
    await callApi('jobapplicant/get-job-data', { jobUuid: uuid }).then((response) => {
      if (response && response.status === 1) {
        setData(response.data);
      } else {
        appContext.tellError('Could not fetch data');
      }
    }).catch(() => {
      appContext.tellError('System error fetching job details');
    });
  }

  function handleFileChange(e, nameSetter) {
    if (e.target && e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      const reader = new FileReader();
      reader.onload = function (event) {
        nameSetter(file.name);
      };
      reader.readAsDataURL(file);
    }
  }

  async function handleSubmitApplication() {
    const selectedCv = cvInputRef.current?.files?.[0];
    const selectedCover = coverInputRef.current?.files?.[0];
    const selectedCert = certInputRef.current?.files?.[0];

    if (selectedCv) {
      if (selectedCover) {
        if (selectedCert) {
          setLoading(true);
          await callApi(
            'jobapplicant/apply-for-job',
            {
              jobUuid: uuid,
              cv: selectedCv,
              coverLetter: selectedCover,
              certifiedCertificates: selectedCert
            }
          ).then(response => {
            if (response && response.status === 1) {
              appContext.tellMessage(response.msg || 'Successful');
              
              setCvName("");
              setCoverName("");
              setCertName("");

              appContext.navBack();
            } else {
              appContext.tellError(response.msg || "Submission context error.");
            }
          }).catch(() => {
            appContext.tellError("Network serialization interface timeout error.");
          });
          setLoading(false);
        } else {
          appContext.tellError("Certified Academic Certificates are required.");
        }
      } else {
        appContext.tellError("A tailored Cover Letter file attachment is required.");
      }
    } else {
      appContext.tellError("Your Curriculum Vitae (CV) file attachment is required.");
    }
  }

  useEffect(() => {
    init();
  }, [uuid]);

  useEffect(() => {
    appContext.setShowOverlayLoader(loading);

    return () => {
      appContext.setShowOverlayLoader(false);
    };
  }, [loading]);

  if (ready) {
    return (
      <div className="container">
        <div className="row">
          
          <div className="col-12 mt-3 mb-3">
            <div className="create-app-panel">
              
              {/* Header Title Grid Row Section */}
              <div className="border-bottom pb-3 mb-4 text-center">
                <h6>{data ? data.title : ''}</h6>
                <div className="d-flex align-items-center justify-content-center gap-2 mb-1">
                  <FiFileText className="text-muted" size={20} />
                  <h4 className="fw-bold mb-0" style={{ color: '#0f172a' }}>Submit Application</h4>
                </div>
                <p className="text-muted small mb-0">Upload all required files to finalize your application registry process.</p>
              </div>

              <div className="row g-1">
                
                {/* UPLOAD SLOT 1: Curriculum Vitae (CV) */}
                <div className="col-12 mb-3">
                  <label className="text-uppercase small fw-bold text-muted d-block mb-2">Curriculum Vitae (CV)</label>
                  <div className="upload-dropzone" onClick={() => cvInputRef.current.click()} style={{ cursor: 'pointer' }}>
                    <FiUploadCloud size={28} className="text-muted mb-2" />
                    <div className="small fw-semibold text-dark">Click to upload your professional CV file</div>
                    <p className="text-muted px-2 small mb-0">PDF or Word Documents accepted</p>
                    
                    {cvName && (
                      <div className="file-attached-pill mt-2">
                        <FiCheckCircle /> {cvName} Staged
                      </div>
                    )}
                  </div>
                  <input type="file" ref={cvInputRef} hidden accept=".pdf,.doc,.docx" onChange={(e) => handleFileChange(e, setCvName)} />
                </div>

                {/* UPLOAD SLOT 2: Cover Letter */}
                <div className="col-12 mb-3">
                  <label className="text-uppercase small fw-bold text-muted d-block mb-2">Cover Letter</label>
                  <div className="upload-dropzone" onClick={() => coverInputRef.current.click()} style={{ cursor: 'pointer' }}>
                    <FiUploadCloud size={28} className="text-muted mb-2" />
                    <div className="small fw-semibold text-dark">Click to attach your Cover Letter</div>
                    <p className="text-muted px-2 small mb-0">PDF or Word Documents accepted</p>
                    
                    {coverName && (
                      <div className="file-attached-pill mt-2">
                        <FiCheckCircle /> {coverName} Staged
                      </div>
                    )}
                  </div>
                  <input type="file" ref={coverInputRef} hidden accept=".pdf,.doc,.docx" onChange={(e) => handleFileChange(e, setCoverName)} />
                </div>

                {/* UPLOAD SLOT 3: Certified Certificates */}
                <div className="col-12 mb-4">
                  <label className="text-uppercase small fw-bold text-muted d-block mb-2">Certified Academic Certificates</label>
                  <div className="upload-dropzone" onClick={() => certInputRef.current.click()} style={{ cursor: 'pointer' }}>
                    <FiUploadCloud size={28} className="text-muted mb-2" />
                    <div className="small fw-semibold text-dark">Click to upload certified certificate</div>
                    <p className="text-muted px-2 small mb-0">PDF or Word Documents Accepted</p>
                    
                    {certName && (
                      <div className="file-attached-pill mt-2">
                        <FiCheckCircle /> {certName} Staged
                      </div>
                    )}
                  </div>
                  <input type="file" ref={certInputRef} hidden accept=".pdf,.doc,.docx" onChange={(e) => handleFileChange(e, setCertName)} />
                </div>

                {/* Final Action Submission Processor Button Row */}
                <div className="col-12 mt-2 text-end">
                  <button 
                    className="submit-btn" 
                    onClick={handleSubmitApplication}
                  >
                    SUBMIT APPLICATION VACANCY <FiArrowRight />
                  </button>
                </div>

              </div>
            </div>
          </div>

        </div>
      </div>
    );
  } else {
    return (
      <div className="container mSupportLoading">
        {getInlineLoader()}
      </div>
    );
  }
}